import "@fontsource/mukta/400.css";
import "@fontsource/mukta/600.css";
import "~/styles/globals.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TransportProvider } from "@connectrpc/connect-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { syncServerClock } from "@yatayat/core/clock";

import { I18nProvider } from "~/lib/i18n";
import { watchInstallOffer } from "~/lib/install";
import { transport } from "~/lib/rpc/transport";

import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: false } },
});

// A new page opens at the top and Back returns to where the reader was. Filters and
// pickers that only change the search pass `resetScroll: false` to keep the place.
const router = createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent", scrollRestoration: true });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Keeps the app on the device so the conductor's scan reopens with no signal. Without
// it, as in development, pages need the network.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}

watchInstallOffer();

const askApiTime = () =>
  fetch("/api/v0/time", { cache: "no-store", credentials: "omit", signal: AbortSignal.timeout(2000) })
    .then((res) => (res.ok ? (res.json() as Promise<{ now: string }>) : Promise.reject(new Error(`HTTP ${res.status}`))))
    .then((body) => body.now);

// Screens take "today" from the api's clock, so the first one waits briefly for it
// rather than open on the device's date.
void syncServerClock(askApiTime).then(() =>
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <TransportProvider transport={transport}>
        <QueryClientProvider client={queryClient}>
          <I18nProvider>
            <RouterProvider router={router} />
          </I18nProvider>
        </QueryClientProvider>
      </TransportProvider>
    </StrictMode>,
  ),
);
